import type { BasicInformationRow } from "./types";

/**
 * Mock dataset for the imported `table-config.json` (reference project's
 * basic information domain), generated deterministically so re-renders and reloads
 * stay stable without a backend or an extra dependency.
 *
 * Any column id in the import that has no dedicated generator below gets a
 * format-matched generic value (number → number, duration → seconds,
 * progress → 0-100, else → text), so adding a column to the import keeps
 * the table fed.
 */

/** Tiny deterministic PRNG (mulberry32) — stable output across reloads. */
function makeRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FIRST_NAMES = [
  "Anh", "Bình", "Chi", "Dũng", "Giang", "Hà", "Hùng", "Lan", "Linh", "Mai",
  "Nam", "Nga", "Phúc", "Quân", "Sơn", "Thảo", "Trang", "Tú", "Vân", "Yến",
];
const LAST_NAMES = [
  "Bùi", "Đặng", "Đỗ", "Hoàng", "Lê", "Nguyễn", "Phạm", "Phan", "Trần", "Vũ",
];
const ROLES = [
  "Engineer", "Senior Engineer", "Team Lead", "Product Manager", "Designer",
  "Analyst", "QA Engineer", "DevOps Engineer",
];
const DEPARTMENTS = [
  "R&D", "Manufacturing", "Quality Assurance", "Operations", "Finance",
  "Procurement", "IT", "Customer Service",
];
const STATUSES: BasicInformationRow["status"][] = ["active", "leave", "retired"];
const LOREM = [
  "Applied to production batch", "Reviewed by supervisor", "Pending approval",
  "Requires second check", "Scheduled for next cycle", "Completed on time",
  "Escalated for review", "Archived automatically",
];

type Generator = (random: () => number, index: number) => unknown;

const generators: Record<string, Generator> = {
  fullName: (random) =>
    `${pick(random, FIRST_NAMES)} ${pick(random, LAST_NAMES)}`,
  email: (_random, index) =>
    `employee${String(index + 1).padStart(4, "0")}@company.test`,
  status: (random) => pick(random, STATUSES),
  role: (random) => pick(random, ROLES),
  department: (random) => pick(random, DEPARTMENTS),
  salary: (random) => Math.round(50000 + random() * 130000),
  quantity: (random) => Math.round(1 + random() * 999),
  timeSpent: (random) => Math.round(1800 + random() * 343800),
  createdAt: (random) => pastIsoDate(random),
  progress: (random) => Math.round(random() * 100),
  capacity: (random) => Math.round(random() * 100),
  completion: (random) => Math.round(random() * 100),
};

/** Format-aware fallback for columns without a dedicated generator. */
function genericValue(columnId: string, random: () => number): unknown {
  if (/dynamic_\d+/.test(columnId)) {
    const template = LOREM[Math.floor(random() * LOREM.length)];
    return `${template} (${columnId})`;
  }
  return `${columnId} value ${Math.round(random() * 9999)}`;
}

function pick<T>(random: () => number, values: T[]): T {
  return values[Math.floor(random() * values.length)];
}

function pastIsoDate(random: () => number): string {
  const past = Date.now() - Math.floor(random() * 5 * 365 * 24 * 3600 * 1000);
  return new Date(past).toISOString();
}

/**
 * Generate `count` basic information rows for the imported config's columns.
 * Selection/row identity keys off the stable `id` field (section 53).
 */
export function generateBasicInformation(count = 2000): BasicInformationRow[] {
  const random = makeRandom(20260917);
  return Array.from({ length: count }, (_, index) => {
    const row: Record<string, unknown> = { id: `employee-${index + 1}` };
    // Column ids follow the imported config's order, incl. dynamic_*.
    for (const [columnId, generator] of Object.entries(generators)) {
      row[columnId] = generator(random, index);
    }
    // 49 dynamic columns per table-config.json.
    for (let i = 1; i <= 49; i++) {
      const columnId = `dynamic_${i}`;
      row[columnId] = genericValue(columnId, random);
    }
    return row as unknown as BasicInformationRow;
  });
}
